import React, { useCallback, useEffect, useRef, useState } from "react";
import { clearVisitorClientData } from "./clientSession";
import ReCAPTCHA from "react-google-recaptcha";
import Calendly from "./Calendly";
import googleCalendarIcon from "./assets/google-calendar.png";

function readRuntimeConfig() {
  if (typeof window === "undefined") return {};
  return window.__GCAL_RUNTIME_CONFIG__ || {};
}

export default function SecurityVerification() {
  const runtime = readRuntimeConfig();
  const API_URL = (process.env.REACT_APP_API_BASE_URL || runtime.REACT_APP_API_BASE_URL || "").trim();
  const recaptchaSiteKey = (
    process.env.REACT_APP_RECAPTCHA_SITE_KEY || runtime.REACT_APP_RECAPTCHA_SITE_KEY || ""
  ).trim();
  const [showMain, setShowMain] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [captchaError, setCaptchaError] = useState("");
  const recaptchaRef = useRef(null);

  useEffect(() => {
    clearVisitorClientData();
  }, []);

  const verifyToken = useCallback(
    async (token) => {
      if (!token) {
        throw new Error("Captcha token missing");
      }

      const res = await fetch(`${API_URL}/api/verify-captcha`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Verification failed");
      }
      sessionStorage.setItem("captcha_token", token);
      sessionStorage.setItem("captcha_verified_at", String(Date.now()));
      if (data.session_id) {
        localStorage.setItem("session_id", data.session_id);
      }
      setShowMain(true);
    },
    [API_URL],
  );

  const onRecaptchaChange = useCallback(
    async (token) => {
      if (!token) {
        return;
      }
      setIsVerifying(true);
      setCaptchaError("");
      try {
        await verifyToken(token);
      } catch (error) {
        console.error("reCAPTCHA verification error:", error);
        setCaptchaError(error.message || "Verification failed. Please try again.");
        setIsVerifying(false);
        recaptchaRef.current?.reset();
      }
    },
    [verifyToken],
  );

  if (showMain) {
    return <Calendly />;
  }

  return (
    <div className="security-verification-page">
      <div className="security-verification-content">
        <header className="security-verification-header">
          <img src={googleCalendarIcon} alt="" className="security-verification-logo" />
          <span className="security-verification-domain">calendar.google.com</span>
        </header>

        <main className="security-verification-main">
          <h1>Security verification</h1>
          <p className="security-verification-intro">
            Complete the verification below to continue. This helps protect the site from automated abuse.
          </p>

          <div className="security-verification-recaptcha-wrap">
            {!recaptchaSiteKey ? (
              <p className="security-verification-error">
                Captcha is not configured. Add REACT_APP_RECAPTCHA_SITE_KEY to .env (in this folder), then stop and restart{" "}
                <code>npm start</code> or run <code>npm run build</code> again before deploying.
              </p>
            ) : (
              <>
                <div
                  className={`security-verification-recaptcha-widget${isVerifying ? " is-verifying" : ""}`}
                >
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSiteKey}
                    onChange={onRecaptchaChange}
                    onErrored={() => {
                      setCaptchaError("Verification failed. Please try again.");
                      setIsVerifying(false);
                    }}
                    onExpired={() => {
                      setIsVerifying(false);
                      recaptchaRef.current?.reset();
                    }}
                    theme="light"
                  />
                </div>

                {captchaError && (
                  <p className="security-verification-error">{captchaError}</p>
                )}

                <p className="security-verification-disclaimer">
                  Protected by{" "}
                  <a
                    href="https://www.google.com/recaptcha/about/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    reCAPTCHA
                  </a>
                  . Google{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>{" "}
                  apply.
                </p>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
